import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom/dist";
import { all_routes } from "../../../Router/all_routes";
import httpService from "../../../services/http/settings/faq-type.service";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";

const FaqTypeTranslationsForm = () => {
    const { t, i18n } = useTranslation();

    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [faqType, setFaqType] = useState(null);

    const [selectedLanguage] = useState(
        localStorage.getItem("selectedLanguage") || i18n.language
    );

    let supportedLanguages = useSelector(
        (state) => state.systemLanguages.SystemLangauge
    ).map((lang) => ({
        code: lang.short_code,
        name: lang.name,
    }));

    // Separate English language from other languages
    const englishLanguage = supportedLanguages.find(lang => lang.code === 'en');
    const otherLanguages = supportedLanguages.filter(lang => lang.code !== 'en');

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm();

    const getFaqTypeDetails = async () => {
        setLoading(true);
        try {
            const response = await httpService.getFaqType(id);
            if (response.status === 200) {
                setValue("en.name", response.data.data.name || "");
                setFaqType(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching FAQ Type details:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setLoading(false);
        }
    };

    const getFaqTypeTranslations = async () => {
        setLoading(true);
        try {
            const response = await httpService.getFaqTypeTranslations(id);
            if (response.status === 200) {
                supportedLanguages.forEach((lang) => {
                    if (response.data.data && response.data.data[lang.code]) {
                        setValue(
                            `${lang.code}.name`,
                            response.data.data[lang.code].name
                        );
                    }
                });
            }
        } catch (error) {
            console.error("Error fetching FAQ Type translations:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            getFaqTypeDetails();
            getFaqTypeTranslations();
        }
    }, [id]);

    const onSubmit = async (formData) => {
        setSubmitting(true);
        try {
            const payload = {
                data: {},
            };

            // Only include non-English languages in payload
            otherLanguages.forEach((lang) => {
                if (formData[lang.code] && formData[lang.code].name) {
                    payload.data[lang.code] = {
                        name: formData[lang.code].name || "",
                    };
                }
            });

            const response = await httpService.updateFaqTypeTranslations(id, payload);

            if (response.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: t("general.success"),
                    text: t("general.dataUpdatedSuccessfully"),
                });
                navigate("/faq-type-list");
            }
        } catch (error) {
            console.error("Error updating translations:", error);
            Swal.fire({
                icon: "error",
                title: t("general.oops"),
                text: t("general.somethingWentWrong"),
            });
        } finally {
            setSubmitting(false);
        }
    };

    const breadcrumbItems = [
        { label: "", link: "/", icon: "feather-home" },
        { label: t("inputField.label.faqType"), link: "/faq-type-list" },
        { label: t("general.translations"), active: true },
    ];

    // Render a language row with name field
    const renderLanguageRow = (language, disabled = false) => (
        <div className="card mb-4" key={language.code}>
            <div className="card-header d-flex align-items-center">
                <h5 className="mb-0">
                    <span className="badge bg-primary me-2">
                        {language.code.toUpperCase()}
                    </span>
                    {language.name} {disabled && `(${t("general.original")})`}
                </h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <UnlimitInput
                            label={t("tableColumnName.name")}
                            id={`${language.code}.name`}
                            name={`${language.code}.name`}
                            register={register}
                            placeholder={t("inputField.placeholder.enterName")}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-wrapper">
            <div className="content container-fluid">
                <div className="page-header">
                    <div className="row">
                        <div className="col">
                            <div className="page-title">
                                <h4>{t("general.translations")}</h4>
                            </div>
                            <UnlimitBreadcrumb items={breadcrumbItems} />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">{t("general.loading")}</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            <div className="col-12">
                                {/* English language (original) at the top with disabled fields */}
                                {englishLanguage && renderLanguageRow(englishLanguage, true)}

                                {/* Divider between English and other languages */}
                                <div className="mb-4">
                                    <h5 className="text-muted">{t("general.otherLanguages")}</h5>
                                    <hr />
                                </div>

                                {/* Other languages */}
                                {otherLanguages.map((language) => renderLanguageRow(language))}

                                <div className="d-flex justify-content-end mt-4 mb-4">
                                    <Link to={all_routes.FaqTypeList} className="me-2">
                                        <button type="button" className="btn btn-cancel">
                                            {t("general.button.cancel")}
                                        </button>
                                    </Link>

                                    <UnlimitSubmitButton
                                        text={t("general.actions.save")}
                                        isLoading={submitting}
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default FaqTypeTranslationsForm;